import { BLOGGER_API_BASE_URL, BLOGGER_BLOG_ID, BLOGGER_API_KEY, BLOGGER_POSTS_URL } from '../constants/blogger';

/** Single blog post from the Blogger API. */
export interface BloggerPost {
    id: string;
    title: string;
    content: string;
    published: string;
    updated: string;
    url: string;
    labels: string[];
    author: {
        displayName: string;
        image: { url: string };
    };
}

/** Response shape from Blogger API posts list endpoint. */
export interface BloggerResponse {
    items?: BloggerPost[];
}

/** Single blog page from the Blogger API (Pages: get). */
export interface BloggerPage {
    kind: string;
    id: string;
    status: 'LIVE' | 'DRAFT';
    blog: {
        id: string;
    };
    published: string;
    updated: string;
    url: string;
    selfLink: string;
    title: string;
    content: string;
    author: {
        id: string;
        displayName: string;
        url: string;
        image: { url: string };
    };
}

/**
 * Singleton manager for Google Blogger API: loads and caches post and page content.
 * Prevents multiple calls to the posts endpoint by storing the result and reusing it.
 */
export class BloggerManager {
    private static instance: BloggerManager;

    /** Cached list of posts; used to skip the REST call when already loaded. */
    private cachedPosts: BloggerPost[] | null = null;

    /** In-flight request promise so concurrent callers share one request. */
    private fetchPromise: Promise<BloggerPost[]> | null = null;

    private constructor() {}

    static getInstance(): BloggerManager {
        if (!BloggerManager.instance) {
            BloggerManager.instance = new BloggerManager();
        }
        return BloggerManager.instance;
    }

    /**
     * Loads all blog posts from the Blogger API.
     * Does not call the endpoint again if the result is already saved in the cache.
     * Concurrent callers receive the same promise so the endpoint is hit at most once.
     */
    async loadAllBlogPosts(): Promise<BloggerPost[]> {
        if (this.cachedPosts !== null) {
            console.log(this.cachedPosts);
            return this.cachedPosts;
        }
        if (this.fetchPromise !== null) {
            return this.fetchPromise;
        }

        this.fetchPromise = (async () => {
            const response = await fetch(BLOGGER_POSTS_URL);
            if (!response.ok) {
                this.fetchPromise = null;
                throw new Error('Failed to fetch blog posts');
            }
            const data: BloggerResponse = await response.json();
            this.cachedPosts = data.items ?? [];
            this.fetchPromise = null;
            return this.cachedPosts;
        })();

        return this.fetchPromise;
    }

    /**
     * Loads a single blog post by post ID from the Blogger API.
     * @param postId The blog post ID
     * @returns The blog post, or throws if not found
     */
    async loadBlogPostById(postId: string): Promise<BloggerPost> {
        const url = `${BLOGGER_API_BASE_URL}/blogs/${BLOGGER_BLOG_ID}/posts/${postId}?key=${BLOGGER_API_KEY}`;
        const response = await fetch(url);        
        if (!response.ok) {
            throw new Error('Failed to fetch blog post');
        }
        const post: BloggerPost = await response.json();
        return post;
    }

    /**
     * Gets a single blog page by page ID from the Blogger API.
     * @param pageId The blog page ID
     * @returns The blog page, or throws if not found
     */
    async getPageFromPost(pageId: string): Promise<BloggerPage> {
        const url = `${BLOGGER_API_BASE_URL}/blogs/${BLOGGER_BLOG_ID}/pages/${pageId}?key=${BLOGGER_API_KEY}`;
        const response = await fetch(url);
        if (!response.ok) {
            throw new Error('Failed to fetch blog page');
        }
        const page: BloggerPage = await response.json();
        return page;
    }
}
